import React, { useRef, useState, useEffect, ReactNode } from 'react';
import { Tooltip } from 'antd';
import type { TooltipProps } from 'antd';
import { stripHtmlForTooltip } from '@/utils';

export interface AppTooltipProps extends Omit<TooltipProps, 'title'> {
  /**
   * Tooltip title/content
   * If `showOnlyOnOverflow` is true and content is not truncated, tooltip won't show
   */
  title?: ReactNode;
  /**
   * Show tooltip only when child content overflows/truncates
   * @default false
   */
  showOnlyOnOverflow?: boolean;
  /**
   * Maximum width of tooltip content
   * @default '300px'
   */
  maxWidth?: string | number;
  /**
   * Maximum height of tooltip content
   * @default '200px'
   */
  maxHeight?: string | number;
}

const AppTooltip: React.FC<AppTooltipProps> = ({
  showOnlyOnOverflow = false,
  title,
  maxWidth = '300px',
  maxHeight = '200px',
  ...props
}) => {
  const childRef = useRef<HTMLElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  // Process title: strip HTML if it's a string
  const processedTitle = typeof title === 'string' ? stripHtmlForTooltip(title) : title;

  useEffect(() => {
    if (!showOnlyOnOverflow) return;

    const checkOverflow = () => {
      if (childRef.current) {
        const { scrollWidth, clientWidth, scrollHeight, clientHeight } = childRef.current;
        const isOverflow = scrollWidth > clientWidth || scrollHeight > clientHeight;
        setIsOverflowing(isOverflow);
      }
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    if (childRef.current) {
      resizeObserver.observe(childRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [title, showOnlyOnOverflow]);

  // Determine whether to show the tooltip
  const shouldShowTooltip = showOnlyOnOverflow ? isOverflowing : true;
  const tooltipTitle = shouldShowTooltip ? processedTitle : '';

  // If child is a single element, clone it with ref
  const child = React.Children.only(props.children);
  if (!React.isValidElement(child)) {
    return (
      <Tooltip
        {...props}
        title={tooltipTitle}
        styles={{
          container: {
            maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight,
            overflow: 'auto',
          },
        }}
        classNames={{ root: 'app-tooltip' }}
      >
        {child}
      </Tooltip>
    );
  }

  const clonedChild = React.cloneElement(child, {
    ref: childRef,
  } as any);

  return (
    <Tooltip
      {...props}
      title={tooltipTitle}
      styles={{
        container: {
          maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight,
          overflow: 'auto',
        },
      }}
      classNames={{ root: 'app-tooltip' }}
    >
      {clonedChild}
    </Tooltip>
  );
};

AppTooltip.displayName = 'AppTooltip';

export default AppTooltip;
